import React, { useCallback, useEffect, useState, useRef } from "react";
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  List,
  ListItem,
  Stack,
  Typography,
  CircularProgress
} from '@mui/material';
import { useInfoStore } from "../../../store/userInfo";
import { isBrowser } from 'react-device-detect';
import { useWindowDimensions } from "../../../App";
import { useLongPress } from "use-long-press";
import { createAyceMenuOrder, getAyceMenuList, getOccupancyNGuest } from "../../../services/order.service";
import { getDiningTimeLimit } from "../../../services/setting.service";
import { EventSourcePolyfill } from "event-source-polyfill";
import authHeader from "../../../api/auth-header";
import SignOut from "../../../components/SignOut";
import { toast } from "react-toastify";
import { IAyceMenu, useOrderStore } from "../../../store/order";
import useIdleTimeout from "../../../context/IdleTimeout";
import { TimerState, useMenuStore } from "../../../store/menuState";
import HorizontalCarousel from "../../promotion";
import { useMenuVisibilityTick } from "../../../hooks/useMenuVisibilityTick";
import { useAppKey } from "../../../context/AppKeyContext";
import { devLog, menuLog, sseLog, logger } from "../../../utils/logger";

// 플랫폼 확인 Android
const isAndroid = /Android/i.test(navigator.userAgent)

export interface IAyceStatus {
  currentAdults: number,
  currentChildren: number,
  tableStatusId?: number,
}

export interface IAyceOrder {
  itemId: number,
  itemName: string,
  itemPrice: number,
  quantity: number,
  itemWithModifierPrice: number,
  subtotalAmount: number
}

export enum TableStatus {
  OPEN = 'OPEN',
  SEATED = 'SEATED',
  ORDERED = 'ORDERED',
  CHECK_REQUESTED = 'CHECK_REQUESTED',
}


export enum status  {
  OPEN = 1,
  SEATED = 2,
  ORDERED = 3,
  CONFIRMED_ALL = 4,  // All orders in a order group are confirmed
  CHECK_REQUESTED = 5,
}

const AyceMenu: React.FC = () => {
  const { userInfo, tableInfo} = useInfoStore();
  const { selectedAyce, setTotalGuest, setSelectedAyce, resetOrderCart } = useOrderStore();
  const { ayceMenus, isNativeFlag, isIdle, totalOrderTimeLimit,setTotalOrderTimeLimit, setIsIdle, setCurrentPage, setAYCEMenus, setVisibleAYCEMenus, setRemainingOrderTime, setTimerState } = useMenuStore();
  const { width, height, orientation } = useWindowDimensions();
  const storeId = userInfo?.storeId;
  const navigate: NavigateFunction = useNavigate();
  const { refreshApp } = useAppKey();
  const promotionSentRef = useRef(false);
  const resetStatusCalledRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const restartTimer = useMenuStore((s) => s.restartTimer);
  const [isOrderMode, setIsOrderMode] = useState(false);
  const [tableAyceStatus, setTableAyceStatus] = useState({currentAdults: 0, currentChildren: 0, tableStatusId: 0} as IAyceStatus);
  const [openSignOut, setOpenSignOut] = useState(false)
  const [isConfirm, setIsConfirm] = useState(false)
  const landMode = (orientation === "landscape") ?true: false
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [inputTime, setImputTime] = useState(0);
  const [showOrderList, setShowOrderList] = useState(false);
  const { idleTimer } = useIdleTimeout({ 
    idleTime: 60, 
    disableIdleTimer: false
  });
  
  // AYCE 메뉴 전용 타이머 체크
  useMenuVisibilityTick();
  
  // 디버깅: 타이머 활성화 상태 확인
  useEffect(() => {
    devLog(`[AyceMenu] Component rendered - selectedAyce: ${!!selectedAyce}`);
  });
  
  // console.log(guests)
  useEffect(() => {
    devLog('[AyceMenu] Component mounted');
    
    async function getAyceMenu() {
      menuLog('🔄 getAyceMenu called');
      if (!storeId) return;
      await getAyceMenuList(storeId as number).then(
        res => {
          menuLog('📊 getAyceMenu response:', res);
          if(res.status === 200 || res.status === 201) {
            menuLog('✅ Setting AYCE menus and calling setVisibleAYCEMenus');
            setAYCEMenus(res.data)
            setSelectedAyce(null)
            setVisibleAYCEMenus()
          } else {
            menuLog('❌ getAyceMenu failed - status not 200/201:', res.status);
          }
        },
        error => {
          menuLog('❌ getAyceMenu error:', error)
        }
      )
    }
    async function getStatusNGuest() {
      await getOccupancyNGuest(tableInfo.id).then(
        res => {
          logger.log(res.data)
          if(res.status === 200 || res.status === 201) {
            setTableAyceStatus(res.data)
            setTotalGuest(res.data?.currentAdults + res.data?.currentChildren)
            if(res.data?.currentAdults + res.data?.currentChildren <= 0){
              setIsOrderMode(false)
            }
            if(!isBrowser) {
              if(window.ReactNativeWebView !== undefined)
                window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'guest', 'data':res.data}));
              else if(window.Android !== undefined)
                window.Android.postMessage(JSON.stringify({'type': 'guest', 'data':res.data}));
            }
            if(res.data && res.data?.currentAdults > 0 && res.data.tableStatusId > 2) {
              setIsLoading(true);
              setShouldShowDialog(false);
              navigate('/main');
            } else {
              setShouldShowDialog(true);
            }
          }
        },
        error => {
          logger.log(error)
        }
      )
    }

    const getAYCETimeLimit = async () => {
      if (!storeId) return;
      const res = await getDiningTimeLimit(storeId as number)
      logger.log('🕐 [getAYCETimeLimit] Server response:', res.data)
      logger.log('🕐 [getAYCETimeLimit] ayceTimeLimitMins (seconds):', res.data.ayceTimeLimitMins)
      logger.log('🕐 [getAYCETimeLimit] Using as seconds directly:', res.data.ayceTimeLimitMins)
      
      if(res.status === 200 || res.status === 201) {
        setTotalOrderTimeLimit(res.data)
        
        // 이미 타이머가 실행 중이면 시간을 리셋하지 않음
        const currentTimerState = useMenuStore.getState().timerState;
        const isTimerRunning = useMenuStore.getState().isTimerRunning;
        
        logger.log('🕐 [getAYCETimeLimit] Current timer state:', currentTimerState)
        logger.log('🕐 [getAYCETimeLimit] Is timer running:', isTimerRunning)
        
        if (currentTimerState === TimerState.RUNNING && isTimerRunning) {
          logger.log('🔄 Timer already running, preserving current time');
          // 타이머 상태만 업데이트하고 시간은 유지
          setTimerState(TimerState.RUNNING);
        } else {
          logger.log('🔄 Timer not running, setting initial time');
          // 타이머가 실행 중이 아니면 초기 시간 설정 (초 단위로 직접 사용)
          const newRemainingTime = res.data.ayceTimeLimitMins; // 초 단위로 직접 사용
          logger.log('🕐 [getAYCETimeLimit] Setting remainingOrderTime to:', newRemainingTime, 'seconds')
          setRemainingOrderTime(newRemainingTime);
          setTimerState(TimerState.RESET);
        }
      }
    }
    getAYCETimeLimit()
    getAyceMenu()
    getStatusNGuest()
    
    return () => {
      devLog('[AyceMenu] Component unmounting');
    };
  },[])

  useEffect(() => {
    const eventSourceInitDict = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        ...authHeader()
      },
      heartbeatTimeout: 100000
    };
    
    const sseUrl = `${process.env.REACT_APP_URI}sse/users?storeId=${storeId}&tableId=${tableInfo.id}`;
    sseLog('🔗 AYCE Connecting to SSE:', sseUrl);
    sseLog('🔑 AYCE Auth headers:', eventSourceInitDict.headers);
    
    const es = new EventSourcePolyfill(sseUrl, eventSourceInitDict);

    es.onopen = () => {
      sseLog("✅ AYCE SSE Connection opened successfully!");
      sseLog("📊 AYCE Connection details:", {
        url: sseUrl,
        storeId,
        tableId: tableInfo.id,
        readyState: es.readyState
      });
    };
    
    es.onerror = (e) => {
      logger.error("❌ AYCE SSE Connection error:", {
        error: e,
        readyState: es.readyState,
        url: sseUrl,
        timestamp: new Date().toISOString()
      });
      
      // Enhanced error categorization
      if (es.readyState === EventSource.CONNECTING) {
        logger.error("🔄 AYCE Connection failed - attempting to reconnect...");
      } else if (es.readyState === EventSource.CLOSED) {
        logger.error("🚫 AYCE Connection closed - check server status and authentication");
      }
    };
    
    es.onmessage = (e) => {
      sseLog("📨 AYCE SSE Message received:", e.data);
      try {
        const esData = JSON.parse(e.data);
        sseLog("📋 AYCE Parsed SSE data:", esData);
        
        switch (esData.type) {
          case 'REFRESH_SCREEN':
            logger.log("🔄 AYCE Refreshing screen...");
            refreshApp();
            break;
          case 'RELOAD_APP':
            if(!isBrowser){
              if (window.Android !== undefined) {
                window.Android.postMessage(JSON.stringify({ type: 'reload' }));
              } else if(window.ReactNativeWebView !== undefined) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'reload' }));
              }
            }
          break;
          case 'AYCE_OCCUPANCY':
            sseLog("👥 AYCE occupancy update:", esData.payload);
            setTableAyceStatus({...esData.payload} as IAyceStatus)
            setTotalGuest(esData.payload?.currentAdults + esData.payload?.currentChildren)
            if(!isBrowser) {
              if(window.ReactNativeWebView !== undefined)
                window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'guest', 'data':esData.payload}));
              else if(window.Android !== undefined)
                window.Android.postMessage(JSON.stringify({'type': 'guest', 'data':esData.payload}));
            }
            setIsOrderMode(true)
            sseLog("✅ AYCE Order mode enabled");
            break;
          case 'TABLE_STATUS':
            sseLog("🪑 AYCE Table status update:", esData.payload);
            if(esData.payload.tableStatusId === 1) {
              sseLog("🔄 AYCE Resetting table status to empty");
              setTableAyceStatus({currentAdults: 0, currentChildren: 0, tableStatusId: 0} as IAyceStatus)
              setIsOrderMode(false)
              setSelectedAyce(null)
              setTotalGuest(0)
            }
            break;
          default:
            sseLog("❓ AYCE Unknown SSE message type:", esData.type);
            return;
        }
      } catch (parseError) {
        sseLog("❌ AYCE Failed to parse SSE message:", {
          error: parseError,
          rawData: e.data,
          timestamp: new Date().toISOString()
        });
      }
    };
    setCurrentPage('ayce')
    sseLog('ayce')
    return () => {
      // SSE 연결 정리 (메모리 누수 방지)
      es.close();
    };
  }, [storeId]);

  const callback = useCallback(() => {
    // if (!isBrowser && window.ReactNativeWebView !== undefined) {
    //   window.ReactNativeWebView.postMessage(JSON.stringify({ 'type': 'play' }));
    // }
    navigate('/storesetting');
  }, [navigate]);

  const bind = useLongPress(callback, {
    threshold: 2000,
    cancelOnMovement: 25,
    cancelOutsideElement: true,
  });

  const callbackLogout = useCallback(() => {
    setOpenSignOut(true)
  }, []);

  const bindLong = useLongPress(callbackLogout, {
    threshold: 2000,
    cancelOnMovement: 25,
    cancelOutsideElement: true,
  });

  const handleDialogClose = () => {
    setIsOrderMode(false)
  }
  
  const handleSelectMenu = async (item: IAyceMenu) => {
    setSelectedAyce(item)
    if(tableAyceStatus.currentAdults === 0 && tableAyceStatus.currentChildren === 0) {
      toast.error('In Progress of setting your menu. Please, wait a minute.')
    }
  }

  const calcTotal = () => {
    const adult  = (selectedAyce!.ayceMainItem.itemPrice* tableAyceStatus.currentAdults)
    const children =  selectedAyce!.ayceSubItem ? (selectedAyce!.ayceSubItem.itemPrice* tableAyceStatus.currentChildren) : 0 ;
    return adult + children
  }

  const handleOrderMenu = async() => {
    const menuId = selectedAyce!.id
    const tableId = tableInfo.id;
    const tableName = tableInfo.tableName
    const totalAmount = calcTotal()
    const totalQuantity =  tableAyceStatus.currentAdults + tableAyceStatus.currentChildren
    if(totalQuantity <= 0) {
      toast.error('The server is entering the number of guests. Please, wait a minute.')
      return;
    }
    if(isConfirm) {
      toast.error('alreay confirm Order')
    }
    setIsConfirm(true)
    setIsLoading(true)
    let orderItems: IAyceOrder[] = [{
      itemId: selectedAyce!.ayceMainItem.id,
      itemName: selectedAyce!.ayceMainItem.itemName,
      itemPrice: selectedAyce!.ayceMainItem.itemPrice,
      quantity: tableAyceStatus.currentAdults,
      itemWithModifierPrice: selectedAyce!.ayceMainItem.itemPrice,
      subtotalAmount: selectedAyce!.ayceMainItem.itemPrice * tableAyceStatus.currentAdults
    }]
    if(tableAyceStatus.currentChildren !== 0) {
      orderItems.push(
        {
          itemId: selectedAyce!.ayceSubItem?.id,
          itemName: selectedAyce!.ayceSubItem?.itemName,
          itemPrice: selectedAyce!.ayceSubItem?.itemPrice,
          quantity: tableAyceStatus.currentChildren,
          itemWithModifierPrice: selectedAyce!.ayceSubItem?.itemPrice,
          subtotalAmount: selectedAyce!.ayceSubItem?.itemPrice * tableAyceStatus.currentChildren
        })
    }

    try {
      if (!storeId) return;
      const res = await createAyceMenuOrder(menuId, tableId, totalAmount, totalQuantity, orderItems, storeId, tableName);
  
      if (res.status === 200 || res.status === 201) {
        toast.success('AYCE was successfully selected. Thank you.');
        // 선택된 AYCE 메뉴를 유지하여 메인 페이지에서 사용
        setSelectedAyce(selectedAyce);
        navigate('/main');
        restartTimer();
      } else {
        toast.error('Failed to process order. Please try again.');
      }
    } catch (error) {
      logger.error('Error while creating order:', error);
      toast.error('An error occurred while processing the order.');
    } finally {
      setIsConfirm(false); // 요청 처리 후 버튼 활성화
      setIsLoading(false);
    }
  }
  
  const resetStatus = () => {
    if (resetStatusCalledRef.current) return;
    resetStatusCalledRef.current = true;
    // if(window.Android !== undefined) {
    //   window.Android.postMessage(JSON.stringify({'type':'play'}));
    // }
    idleTimer.reset();
    setIsIdle(false);
    resetOrderCart();
    setTimeout(() => {
      resetStatusCalledRef.current = false;
      promotionSentRef.current = false;
    }, 1000);
  }

  useEffect(() => {
    if (isIdle && isNativeFlag && !promotionSentRef.current) {
      idleTimer.pause();
      window.Android?.postMessage(JSON.stringify({ type: 'promotions' }));
      promotionSentRef.current = true;
    }
  }, [isIdle]);
  
  useEffect(() => {
    const setNativeData = (event: MessageEvent) => {
      devLog("Raw Event Data:", event.data);
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        devLog("Parsed Data:", data);
        if (data.action === "endPromotion") {
          resetStatus()
          devLog("Promotion End:", data.action);
        }
      } catch (error) {
        logger.error("Error parsing message:", error, event.data);
      }
    };

    window.addEventListener("message", setNativeData);
    return () => window.removeEventListener("message", setNativeData);
  }, []);

  return (
    <>
    { (isIdle && !isNativeFlag && !showOrderList) ? 
        <HorizontalCarousel/>
      :
      <section style={{  
        backgroundColor: '#F72E13',
        width: '100vw',
        height: height
      }}>
          {openSignOut && <SignOut openSignOut={openSignOut} setOpenSignOut={setOpenSignOut} />}
          {isLoading && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', height: '12.4rem' }}>
            <Typography sx={{fontSize: '5.0rem', fontWeight: '700', fontStyle: 'normal', mt: '3.1rem', color: '#fff'}} {...bindLong()}>Choose the menu</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', height: '6.1rem' }}>
                  {/* <Notice /> */}
                  <Box
                    sx={{
                      position: 'absolute', right: `1.5rem`, backgroundColor: '#000', borderRadius: '0px 0px 1.5rem 1.5rem',
                      display: 'flex', maxWidth: '29.3rem', height: landMode ? '6.1rem': '4.4rem', padding: '.5rem 1.4rem', flexDirection: 'row', ml: '6rem',
                      alignItems: 'center', gap: '1rem', flexShrink: 0
                    }}
                    {...bind()}
                  >
                    <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontStyle: 'normal', fontWeight: '700', lineHeight: '2.2rem', letterSpacing: '-.018rem', width: landMode ?'5.5rem': '8.8rem' }}>
                      Table No.
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '2.6rem', fontStyle: 'normal', fontWeight: '700', letterSpacing: '-.026rem', textAlign: 'left' }}>
                      {tableInfo.tableName}
                    </Typography>
                  </Box>
                </Box>
          </Box>
          <Box sx={{display: 'flex', flexDirection: landMode ? 'row' : 'column', height: landMode ? '80%':'90%', padding: '2.7rem 2.4rem', justifyContent: 'center', alignItems: 'flex-start', gap: '2rem' }}>
            <Box sx={{width : landMode ? '70%': '100%'}}>
              <Box sx={{p: '2.9rem 2.7rem 0rem 2.7rem', backgroundColor:'#F1F1F1', borderRadius: '1.0rem', height: landMode ? `calc(${height/10}rem - 21.5rem)`: `100%`,display: "flex",  justifyContent: 'center', width :'100%'}}>
                <List sx={{ display: "flex",
                      flexDirection: "row",
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      width: '100%',
                      flex: '0 0 100%',
                      flexFlow: "wrap",
                      gap: '1rem',
                      overflow: "overlay",
                      overflowX: 'hidden',
                      maxHeight: landMode? '90%': '100%',
                      padding: '0rem',
                      pl: '.4rem',
                      paddingRight: '.4rem',
                      paddingBottom: '2.0rem'}}>
                    {ayceMenus.map((item: IAyceMenu) => {
                      return( item.visible &&
                        <ListItem key={item.id} sx={{p: '0.5rem', flexDirection: 'column', flex: `1 1 calc((100% - 1rem)/2)`, minWidth: '25.8rem',
                          width: `calc((100% - 1rem)/2)` ,
                          maxWidth: `calc((100% - 1rem)/2)` ,
                          height: 'auto', borderRadius: '1.0rem',  backgroundColor: selectedAyce?.id === item.id ? '#F72E13': '#fff', justifyContent: 'flex-start', alignItems: 'flex-start'}}
                          onClick={() =>  handleSelectMenu(item)}>
                          {/* <Box sx={[{width: '100%'}, {backgroundColor: selectedAyce?.id === item.id ? '#F72E13': '#fff'}]}> */}
                            <Stack sx={{ width: `100%` , gap: '4.0rem'}} >
                              <Box sx={{height: '7.6rem', padding: '1.2rem 2.0rem',  display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                                <Typography sx={{color: selectedAyce?.id === item.id ? '#fff': '#F72E13', fontWeight: '700', fontSize: '3.0rem', lineHeight: 'normal'}}>{item.menuName}</Typography>
                                <img style={{width: '4.5rem', height: '4.5rem'}}
                                  src={selectedAyce?.id === item.id ?  process.env.PUBLIC_URL+'/assets/images/check_sel_c.svg' : process.env.PUBLIC_URL+'/assets/images/check_c.svg'}
                                  alt="check_c" />
                              </Box>
                              <Stack sx={{width: '100%',  padding: '2.2rem 2.8rem'}}>
                                <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between',  height: '3.9rem'}}>
                                  <Typography style={{color: selectedAyce?.id === item.id ? '#fff': '#000', fontWeight: '500', fontSize: '2.6rem', lineHeight: 'normal', }}>Adult</Typography>
                                  <Typography sx={{color: selectedAyce?.id === item.id ? '#fff': '#000', fontWeight: '500', fontSize: '2.6rem', lineHeight: 'normal', textAlign: 'end'}}> ${(item.ayceMainItem.itemPrice/100).toFixed(2)}</Typography>
                                </Box>
                                {item.ayceSubItem ? <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', height: '3.9rem'}}>
                                  <Typography style={{color: selectedAyce?.id === item.id ? '#fff': '#000', fontWeight: '500', fontSize: '2.6rem', lineHeight: 'normal', }}>Child</Typography> 
                                  <Typography sx={{color: selectedAyce?.id === item.id ? '#fff': '#000', fontWeight: '500', fontSize: '2.6rem', lineHeight: 'normal', textAlign: 'end'}}>${(item.ayceSubItem.itemPrice/100).toFixed(2)}</Typography>
                                </Box> : <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', height: '3.9rem'}}/>}
                              </Stack>
                            </Stack>
                          {/* </Box> */}
                        </ListItem>)
                      })}
                </List>
              </Box>
            </Box>
            <Box  sx={{display: 'flex', position: 'relative', flexDirection: landMode? 'column': 'row', width: landMode ? '30rem': '100%', justifyContent: landMode? 'flex-start': 'space-between',
              height: landMode ? `calc(${height/10}rem - 21.5rem)`: `26%`, padding: '2.4rem 2.6rem', borderRadius: '1.5rem', backgroundColor: '#fff', gap: '3rem'}}>
              <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: landMode?'center': 'flex-start', alignItems: 'flex-start'}}>
                <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', height: '4.7rem' , alignItems: 'center'}}>
                  <img style={{width: '3.0rem', height: '3.0rem'}}
                    src={process.env.PUBLIC_URL+'/assets/images/user.svg'}
                    alt="guest"
                  />
                  <Typography sx={{fontSize: '2.6rem', fontWeight: '600', ml: '1.0rem' }}>Adult</Typography>
                  <Typography sx={{fontSize: '2.6rem', fontWeight: '600', ml: '3.7rem', height: '4.7rem' , width: '4.7rem', padding: '.4rem .2rem', backgroundColor: '#C5C5C5', 
                    color: '#fff', textAlign: 'center', borderRadius: '.8rem' }}>{tableAyceStatus.currentAdults}</Typography>
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', height: '4.7rem' , mt: '1.5rem' }}>
                  <img style={{ width: '2.4rem', height: '2.4rem'}}
                    src={process.env.PUBLIC_URL+'/assets/images/user.svg'}
                    alt="guest"
                  />
                  <Typography sx={{fontSize: '2.6rem', fontWeight: '600', ml: '1.6rem'}}>Child</Typography>
                  <Typography sx={{fontSize: '2.6rem', fontWeight: '600', ml: '3.7rem', height: '4.7rem' , width: '4.7rem', padding: '.4rem .2rem', backgroundColor: '#C5C5C5', 
                    color: '#fff', textAlign: 'center', borderRadius: '.8rem'}}>{tableAyceStatus.currentChildren}</Typography>
                </Box>

              </Box>
              { selectedAyce ?
                  (landMode ?
                    <Stack sx={{
                      position: 'absolute', 
                      bottom: '0rem', 
                      left: 0,
                      right: 0,
                      width: '100%',
                      padding: '0 2.6rem'  // 좌우 여백 추가
                    }}>
                      <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                        <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: 'rgba(0, 0, 0, 0.50)'}}>x{tableAyceStatus.currentAdults}</Typography>
                        <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: 'rgba(0, 0, 0, 0.50)', width: '13.3rem', textAlign: 'end' }}>${(selectedAyce!.ayceMainItem.itemPrice/100).toFixed(2)}</Typography>
                      </Box>
                      {selectedAyce!.ayceSubItem && <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '1.2rem'}}>
                        <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: 'rgba(0, 0, 0, 0.50)'}}>x{tableAyceStatus.currentChildren}</Typography>
                        <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: 'rgba(0, 0, 0, 0.50)', width: '13.3rem', textAlign: 'end' }}>${(selectedAyce!.ayceSubItem.itemPrice/100).toFixed(2)}</Typography>
                      </Box>}
                      <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '1.2rem'}}>
                        <Typography sx={{fontSize: '2.6rem', fontWeight: '600', textAlign: 'right', color: '#000' }}>Subtotal</Typography>
                        <Typography sx={{fontSize: '2.6rem', fontWeight: '700', textAlign: 'right', color: '#F72E13' }}>${(calcTotal()/100).toFixed(2)}</Typography>
                      </Box>
                      <Button disabled={tableAyceStatus.currentAdults+tableAyceStatus.currentChildren === 0 || isConfirm} sx={{ padding: '2.0rem 4.0rem', background: '#F72E13', borderRadius: '6.0rem', height: '5.9rem', mt: '1.0rem', mb: '2.8rem', }}
                        onClick={() => handleOrderMenu()}>
                        <Typography sx={{fontWeight: '600', fontSize: '3.0rem', color: '#fff', width: '100%'}}>confirm</Typography>
                      </Button>
                    </Stack>:
                    <Stack sx={{ display: 'flex', flexDirection: 'row', flex: 1, gap: '2rem' , alignItems: 'flex-start', justifyContent: 'space-between'}}>
                      <Stack sx={{display: 'flex', flexDirection: 'column', flex: 1}}>
                        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', height: '4.7rem', mt: '0.7rem' }}>
                          <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: 'rgba(0, 0, 0, 0.50)'}}>x{tableAyceStatus.currentAdults}</Typography>
                          <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: 'rgba(0, 0, 0, 0.50)', width: '13.3rem', textAlign: 'end' }}>${(selectedAyce!.ayceMainItem.itemPrice/100).toFixed(2)}</Typography>
                        </Box>
                        {selectedAyce!.ayceSubItem ? <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '1.5rem', height: '4.7rem'}}>
                          <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: 'rgba(0, 0, 0, 0.50)'}}>x{tableAyceStatus.currentChildren}</Typography>
                          <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: 'rgba(0, 0, 0, 0.50)', width: '13.3rem', textAlign: 'end' }}>${(selectedAyce!.ayceSubItem.itemPrice/100).toFixed(2)}</Typography>
                        </Box> : <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '1.5rem', height: '4.7rem'}}/>}
                        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '1.2rem'}}>
                          <Typography sx={{fontSize: '2.6rem', fontWeight: '600', textAlign: 'right', color: '#000' }}>Subtotal</Typography>
                          <Typography sx={{fontSize: '2.6rem', fontWeight: '700', textAlign: 'right', color: '#F72E13', ml: '0.5rem' }}>${(calcTotal()/100).toFixed(2)}</Typography>
                        </Box>
                        <Button disabled={tableAyceStatus.currentAdults+tableAyceStatus.currentChildren === 0 || isConfirm} sx={{ padding: '1.0rem 2.0rem', background: '#F72E13', borderRadius: '6.0rem', height: '6.9rem', mt: '2.8rem'}}
                          onClick={() => handleOrderMenu()}>
                          <Typography sx={{fontWeight: '600', fontSize: '3.0rem', color: '#fff'}}>confirm</Typography>
                        </Button>
                      </Stack>
    
                    </Stack>)
                : <Box sx={{marginTop: '32.5rem'}} />
              }
            </Box>
          </Box>
        {isOrderMode ? 
          <Dialog open={isOrderMode} PaperProps={{ sx :{justifyContent: 'center', alignItems: 'center', width: '63.9rem', height: '33.8rem', overflow: 'hidden', borderRadius: '1.5rem' } }}>
            <Box sx={{display: 'contents', justifyContent: 'center', padding: '5.0rem 9.0rem', gap: '4.0rem', alignItems: 'flex-start'}}>
              <Typography component="p" sx={{fontSize: '3.4rem', fontWeight: '600', fontStyle: 'normal', maxHeight: '18.0rem', textAlign: 'center', 
                lineHeight: '150%', letterSpacing: '-.044', whiteSpace: 'pre-line'}}>{`All set! \n Please choose a menu \n to start your order!`}</Typography> 
              <Button sx={{ padding: '2.0rem 4.0rem', background: '#F72E13', borderRadius: '6.0rem', height: '6.0rem', mt: '4.0rem'}}
                onClick={() => handleDialogClose()}>
                <Typography sx={{fontWeight: '500', fontSize: '3.0rem', color: '#fff', textTransform: 'none'}}>Choose the menu</Typography>
              </Button>
            </Box>
          </Dialog>
        : shouldShowDialog && tableAyceStatus?.currentAdults <= 0 && !isConfirm ?
          <Dialog open={!isOrderMode} PaperProps={{ sx :{justifyContent: 'center', alignItems: 'center', width: '63.9rem', height: '33.8rem', overflow: 'hidden', borderRadius: '1.5rem' } }} {...bindLong()}>
            <Box sx={{display: 'contents', justifyContent: 'center', padding: '5.0rem 9.0rem', gap: '4.0rem', alignItems: 'flex-start'}}>
              <Typography component="p" sx={{fontSize: '3.4rem', fontWeight: '600', fontStyle: 'normal', maxHeight: '18.0rem', textAlign: 'center', 
                lineHeight: '150%', letterSpacing: '-.044', whiteSpace: 'pre-line'}}>{`Please wait.\n The servers will \n set the guest count. `}</Typography> 
            </Box>
          </Dialog> : <></>
        }
        {/* {totalOrderTimeLimit > 0 &&
          <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', height: '4.7rem',
            position: 'absolute',
            bottom: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#fff',
            padding: '10px',
            borderRadius: '5px'
           }}
          >
          <img style={{ width: '2.4rem', height: '2.4rem', backgroundColor: '#000'}}
            src={process.env.PUBLIC_URL+'/assets/images/time.svg'}
            alt="time"
          />
          <Typography sx={{fontSize: '1.8rem', fontWeight: '600', ml: '1rem'}}>Order Time Out</Typography>
          <Typography sx={{fontSize: '2rem', fontWeight: '600', ml: '2rem', height: '4rem' , width: '4rem', padding: '.4rem .4rem', backgroundColor: '#C5C5C5', 
            color: '#fff', textAlign: 'center', borderRadius: '.8rem'}}>{String(Math.floor(totalOrderTimeLimit/3600)).padStart(2, '0')}</Typography>
          <Typography sx={{fontSize: '2rem', fontWeight: '600', mx: '1rem'}}>:</Typography>
          <Typography sx={{fontSize: '2rem', fontWeight: '600', height: '4rem' , width: '4rem', padding: '.4rem .4rem', backgroundColor: '#C5C5C5', 
            color: '#fff', textAlign: 'center', borderRadius: '.8rem'}}>{String(Math.floor((totalOrderTimeLimit%3600)/60)).padStart(2, '0')}</Typography>
          </Box>} */}
      </section>  
    }
    </>
  );
}

export default AyceMenu;

